import type { QueryRowType } from "@rocicorp/zero";
import type { ReactNode } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import type { GrantEntry } from "../../../shared/rbac/roles";
import { useConnectorDisconnectGuard } from "../../hooks/useConnectorDisconnectGuard";
import { PersonalUseCard } from "../PersonalUseCard";
import { connectorRoleDefaultsForSpace } from "../../lib/connectorGrantsForSpace";
import type { ScopeAccessPendingHandle } from "../ScopeAccessTable";
import { SupplierAccessTab } from "../SupplierAccessTab";

type Connector = NonNullable<
  QueryRowType<ReturnType<typeof queries.loadConnectorDetails>>
>;

type Props = {
  app: Connector;
  canManage: boolean;
  canManagePersonalUse: boolean;
  stagedGrantsRef?: { current: ScopeAccessPendingHandle | null };
  onEdit?: () => void;
  localGrants?: GrantEntry[];
  onChangeLocalGrants?: (grants: GrantEntry[]) => void;
  newPrincipalIds?: ReadonlySet<string>;
  requestReason?: ReactNode;
};

export function AccessTab({
  app,
  canManage,
  canManagePersonalUse,
  stagedGrantsRef,
  onEdit,
  localGrants,
  onChangeLocalGrants,
  newPrincipalIds,
  requestReason,
}: Props) {
  const z = useZero();
  const [groupAccess] = useQuery(
    queries.connectedAppGroupAccess({ connectedAppId: app.id })
  );
  const { guardCommit, modal: guardModal } = useConnectorDisconnectGuard({
    appId: app.id,
    appName: app.name,
    scopeGrants: app.roleGrantsAsScope,
  });
  return (
    <SupplierAccessTab
      scopeId={app.id}
      scopeKind="connected_app"
      scopeGrants={app.roleGrantsAsScope}
      supplierRelation="space_uses_connected_apps"
      roleDefaultsForSpace={connectorRoleDefaultsForSpace}
      resourceLabel="connector"
      accessDescription="Add members and groups, and choose their role."
      groupAccess={groupAccess}
      canManage={canManage}
      onEdit={onEdit}
      stagedGrantsRef={stagedGrantsRef}
      localGrants={localGrants}
      onChangeLocalGrants={onChangeLocalGrants}
      newPrincipalIds={newPrincipalIds}
      guardCommit={guardCommit}
      guardModal={guardModal}
      accessFooter={requestReason}
    >
      <PersonalUseCard
        resourceLabel="connector"
        personalUse={app.personalUse}
        canManage={canManagePersonalUse}
        onChange={(personalUse) =>
          z.mutate(
            sharedMutators.setConnectedAppPersonalUse({
              connectedAppId: app.id,
              personalUse,
            })
          )
        }
      />
    </SupplierAccessTab>
  );
}
